// src/pages/ChooseOwnPassword.jsx
// After signing in with a temporary password (from the welcome email),
// people must choose their own before anything else. Two-factor setup
// comes next. The server enforces the same order.
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import AuthShell, { A, AuthMessage, PasswordField } from '../components/AuthShell';
import { describeAuthError } from '../utils/authErrors';

export function passwordProblem(pw) {
  if (pw.length < 8) return 'Please use at least 8 characters.';
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return 'Please use both letters and numbers.';
  return null;
}

export default function ChooseOwnPassword() {
  const { user, userInfo, logout, replaceTemporaryPassword } = useAuth();
  const [pw, setPw]       = useState('');
  const [again, setAgain] = useState('');
  const [current, setCurrent] = useState('');
  const [askCurrent, setAskCurrent] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  async function submit(e) {
    e.preventDefault();
    setErr('');
    const bad = passwordProblem(pw);
    if (bad) { setErr(bad); return; }
    if (pw !== again) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    try {
      await replaceTemporaryPassword(pw, askCurrent ? current : undefined);
    } catch (e2) {
      if (e2.code === 'auth/requires-recent-login') { setAskCurrent(true); setErr('For your security, please also enter the temporary password from the email.'); }
      else setErr(e2.code ? describeAuthError(e2, 'reauth').message : e2.message);
    }
    setBusy(false);
  }

  return (
    <AuthShell stack>
      <h1 style={S.title}>Choose your password</h1>
      <p style={S.text}>You signed in with a temporary password. Please choose your own to continue. Next, you'll set up two-factor authentication{userInfo?.askProfile ? ' and check your profile' : ''}.</p>
      <form onSubmit={submit} style={S.form}>
        {askCurrent && (
          <label style={S.label}>Temporary password (from the email)
            <PasswordField value={current} onChange={e => setCurrent(e.target.value)} required autoComplete="current-password" />
          </label>
        )}
        <label style={S.label}>New password
          <PasswordField value={pw} onChange={e => setPw(e.target.value)} required autoFocus autoComplete="new-password"
            placeholder="At least 8 characters, letters and numbers" />
        </label>
        <label style={S.label}>Type it again
          <PasswordField value={again} onChange={e => setAgain(e.target.value)} required autoComplete="new-password" />
        </label>
        {err && <AuthMessage tone="error">{err}</AuthMessage>}
        <button type="submit" disabled={busy} className="auth-btn">{busy ? 'Saving…' : 'Save password and continue'}</button>
      </form>
      <div style={S.footerRow}>
        <span style={S.signedInAs}>Signed in as {user?.email}</span>
        <button onClick={logout} style={S.link} className="auth-link">Sign out</button>
      </div>
    </AuthShell>
  );
}


export const S = {  // 3.40.0: the page and card come from AuthShell; the rest follows its look (A)
  title: { ...A.title, margin: 0 },
  text: { ...A.text, margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, ...A.label },
  input: { ...A.input, marginBottom: 0 },
  footerRow: { ...A.footerRow, marginTop: 6 },
  signedInAs: A.signedInAs,
  link: A.link,
};
